/**
 * Sign in, and the second-factor challenge.
 *
 * # Against the implementation, not against the published contract
 *
 * `docs/api/openapi/v1.yaml` describes a session cookie named
 * `__Host-exigit_session` and a CSRF double-submit token in `X-ExiGit-CSRF`.
 * The running server has neither: `internal/api/handlers.go` sets a cookie
 * named by `SESSION_COOKIE_NAME` (default `exigit_session`) and
 * `internal/api/auth.go` defends writes with `Origin` / `Sec-Fetch-Site`
 * instead. This screen is built against what the server does. The divergence
 * is recorded in web/README.md; the contract is not edited from here.
 *
 * # Why the two states are one file
 *
 * Because they are one flow and the transition between them is the part that
 * breaks. `POST /v1/auth/sign-in` answers 204 whether or not a second factor
 * is pending; the only way to find out is to call `GET /v1/users/me` and see
 * whether it answers `mfa_required`. Keeping both states here means that
 * transition is visible in one place rather than spread across a redirect.
 */

import { useState } from 'react';

import { describeFailure, isApiError, isMFARequired } from '~/api/problem';
import { useSessionContext } from '~/auth/session';
import { Alert, Button, Field, Icon, Input } from '~/components';

import styles from './SignIn.module.css';

export function SignIn({ expired = false }: { expired?: boolean } = {}) {
  const { signIn, isSigningIn } = useSessionContext();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<unknown>(null);

  async function onSubmit(event: React.SyntheticEvent) {
    event.preventDefault();
    setError(null);
    try {
      await signIn({ email, password });
    } catch (cause) {
      setError(cause);
    }
  }

  return (
    <AuthFrame
      title="Sign in to Exi Git"
      subtitle="Use the email address and password for your Exi Git account."
    >
      <form className={styles.form} onSubmit={(event) => void onSubmit(event)} noValidate>
        {/* Why they are looking at this form, when they were working a moment
         * ago. Without it an expired session is indistinguishable from having
         * never signed in, and the user is left to guess what they did. It
         * yields to a real refusal: once they have tried and been told why,
         * the stale explanation is noise. */}
        {expired && !error ? (
          <Alert tone="neutral" title="Your session ended">
            Sign in again to pick up where you were.
          </Alert>
        ) : null}
        {error ? <SignInProblem error={error} /> : null}

        <Field label="Email address">
          <Input
            type="email"
            name="email"
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
            }}
            // The browser's own autofill, which is how a password manager
            // finds this form. A sign-in form that fights the password manager
            // is a sign-in form that trains people to type passwords by hand.
            autoComplete="username"
            required
          />
        </Field>

        <Field label="Password">
          <Input
            type="password"
            name="password"
            value={password}
            onChange={(event) => {
              setPassword(event.target.value);
            }}
            autoComplete="current-password"
            required
          />
        </Field>

        <Button type="submit" variant="primary" loading={isSigningIn} className={styles.submit}>
          {isSigningIn ? 'Signing in…' : 'Sign in'}
        </Button>
      </form>
    </AuthFrame>
  );
}

/* ====================================================================== *
 * The second factor
 * ====================================================================== */

export function MFAChallenge() {
  const { verifyMFA, signOut, isSigningOut } = useSessionContext();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  async function onSubmit(event: React.SyntheticEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await verifyMFA(code);
    } catch (cause) {
      setError(cause);
      setCode('');
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthFrame
      title="One more step"
      subtitle="Enter the code from your authenticator app, or one of your recovery codes."
    >
      <form className={styles.form} onSubmit={(event) => void onSubmit(event)} noValidate>
        {error ? <SignInProblem error={error} fallbackTitle="That code was not accepted" /> : null}

        <Field
          label="Verification code"
          hint="A six-digit code from your app, or a recovery code if you cannot reach it."
        >
          <Input
            name="code"
            value={code}
            onChange={(event) => {
              setCode(event.target.value);
            }}
            // `one-time-code` is what lets iOS and Android offer the code from
            // the notification. `inputMode` stays text rather than numeric
            // because a recovery code is not digits.
            autoComplete="one-time-code"
            required
            spellCheck={false}
          />
        </Field>

        <Button type="submit" variant="primary" loading={busy} className={styles.submit}>
          {busy ? 'Verifying…' : 'Verify'}
        </Button>

        {/* Sign-out is reachable from here because the server deliberately
         * allows it: `mfaChallengeRoute` in internal/api/auth.go admits
         * `POST /v1/auth/sign-out` so that *"a user who cannot complete a
         * challenge — the phone is at home — must still be able to end the
         * session rather than leave a live one behind"*. */}
        <Button
          variant="ghost"
          disabled={isSigningOut}
          onClick={() => {
            void signOut();
          }}
        >
          Sign out instead
        </Button>
      </form>
    </AuthFrame>
  );
}

/**
 * A refusal on this page, worded by the contract.
 *
 * Sign-in is where the wording matters most, because the three things that go
 * wrong here mean completely different things to the person at the keyboard:
 * the password is wrong, they have tried too often, or an abuse control is
 * down and nobody can sign in at all. `describeFailure` separates the three;
 * this renders them.
 *
 * A 401 is the exception that needs overriding. Everywhere else in the product
 * a 401 means "your session ended, sign in again", which is nonsense on the
 * sign-in form itself — here it means the credentials were refused, and the
 * server's own detail says so better than the generic sentence does.
 */
function refusalDetail(error: unknown): string | undefined {
  return isApiError(error) ? error.problem.detail : undefined;
}

function SignInProblem({ error, fallbackTitle }: { error: unknown; fallbackTitle?: string }) {
  const failure = describeFailure(error);
  const credentialsRefused = failure.reauthenticate && !isMFARequired(error);

  return (
    <Alert
      tone={failure.tone}
      // Assertive whatever the tone. Every failure on this form means the one
      // thing — you are not signed in — and the user is standing at a button
      // they just pressed, waiting to hear whether it worked. A rate limit is
      // styled as a warning rather than a danger because it is not the user's
      // mistake; that is a question of colour and has nothing to do with how
      // urgently it needs saying.
      live="assertive"
      title={credentialsRefused ? (fallbackTitle ?? 'Sign-in failed') : failure.title}
    >
      {credentialsRefused
        ? (refusalDetail(error) ?? 'Those credentials were not accepted.')
        : failure.message}
    </Alert>
  );
}

/* ====================================================================== *
 * The frame
 * ====================================================================== */

function AuthFrame({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle: string;
  children: React.ReactNode;
}) {
  return (
    <div className={styles.page}>
      <main className={styles.panel}>
        <div className={styles.brand}>
          <Icon.Mark size={24} className={styles.brandMark} />
          <span className={styles.brandName}>Exi Git</span>
        </div>
        <h1 className={styles.title}>{title}</h1>
        <p className={styles.subtitle}>{subtitle}</p>
        {children}
      </main>
    </div>
  );
}
